import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import SimplePanel from '@/shared/components/SimplePanel';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import Button from '@/shared/components/Button';
import { ScrollableTable, ColumnHeader } from '@/shared/components/table';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import userStorage from '@/shared/services/userStorage';
import {
  initHiddenColumnsState,
  handleHiddenColumnsSelection,
} from '@/shared/utils/hiddenColumnsHelper';
import api from '@/shared/api';
import SignalRService from '@/shared/services/SignalRService';
import DataCacheService from '@/shared/services/DataCacheService';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import { FaCog, FaFileExcel, FaSpinner } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import { injectStateService, StateService } from '@/shared/services/StateService';
import Checkbox from '@/shared/components/input/Checkbox';
import EmployeeOverviewFilters, { defaultFilters } from './EmployeeOverviewFilters';
import EmployeeOverviewRow from './EmployeeOverviewRow';
import SelectPeriodModal from './modals/SelectPeriodModal';
import DeleteEmployeeModal from './modals/DeleteEmployeeModal';
import RestoreEmployeeModal from './modals/RestoreEmployeeModal';
import EmployeeGlobalActionsButton from './EmployeeGlobalActionsButton';
import UpdateEmployeeAmountModal from './modals/UpdateEmployeeAmountModal';
import UpdateEmployeeProfileModal from './modals/UpdateEmployeeProfileModal';
import UpdateEmployeeTagsModal from './modals/UpdateEmployeeTagsModal';
import messages from './employeeMessages';
import { dynamicSort } from '@/shared/utils/dynamicSort';

const columnSelectionStorageKey = 'EmployeeOverviewHiddenColumnsKey';
const overviewParamsStorageKey = 'EmployeeOverviewParams';

const columnDefinitions = [
  { id: 'phone', message: messages.Phone, visibleByDefault: true },
  { id: 'email', message: messages.Email, visibleByDefault: true },
  { id: 'dateOfBirth', message: messages.DateOfBirth, visibleByDefault: true },
  { id: 'profile', message: messages.PriceProfile, visibleByDefault: true },
  { id: 'company', message: messages.OwnCompany, visibleByDefault: true },
  { id: 'teams', message: messages.Teams, visibleByDefault: true },
  { id: 'role', message: messages.Role, visibleByDefault: true },
  { id: 'employeeType', message: messages.EmployeeType, visibleByDefault: true },
  { id: 'employeeNumber', message: messages.EmployeeNumber, visibleByDefault: false },
  { id: 'startDate', message: messages.EmployedSince, visibleByDefault: true },
  { id: 'endDate', message: messages.EndDate, visibleByDefault: true },
  { id: 'employmentStartDate', message: messages.EmploymentStartDate, visibleByDefault: false },
  { id: 'employmentEndDate', message: messages.EmploymentEndDate, visibleByDefault: false },
  { id: 'license', message: messages.License, visibleByDefault: true },
  { id: 'confirmed', message: messages.Confirmed, visibleByDefault: true },
  { id: 'twoFaEnabled', message: messages.TwoFaEnabled, visibleByDefault: true },
  {
    id: 'internalPrice',
    message: messages.InternalPrice,
    visibleByDefault: false,
    enabled: () =>
      AuthService.hasPermission({
        functionality: functionalities.EmployeeInternalPrice,
        privilege: privileges.Read,
      }),
  },
  {
    id: 'intraCompanyPrice',
    message: messages.IntraCompanyPrice,
    visibleByDefault: false,
    enabled: () =>
      AuthService.hasPermission({
        functionality: functionalities.EmployeeInternalPrice,
        privilege: privileges.Read,
      }),
  },
];
const initSearchParams = () => {
  const stored = userStorage.getItem(overviewParamsStorageKey);
  return {
    page: 0,
    pageSize: 50,
    desc: false,
    sortKey: ['firstName', 'lastName'],
    filter: stored?.table?.filter || {},
    search: stored?.table?.search || {},
    select: stored?.table?.select || {},
    employeeFilters: stored?.filters ? { ...defaultFilters, ...stored.filters } : defaultFilters,
  };
};

const EmployeeOverviewContainer = ({ intl, stateService }) => {
  const hiddenColumnsState = useMemo(
    () => initHiddenColumnsState(columnSelectionStorageKey, columnDefinitions, intl),
    [intl],
  );

  const [hideableColumns, setHideableColumns] = useState(hiddenColumnsState.hideableColumns);
  const [columnStatus, setColumnStatus] = useState(hiddenColumnsState.columnStatus);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showSelectPeriodModal, setShowSelectPeriodModal] = useState(false);
  const [selectPeriodSaving, setSelectPeriodSaving] = useState(false);
  const [showDeleteEmployeeModal, setShowDeleteEmployeeModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [deleteEmployeeSaving, setDeleteEmployeeSaving] = useState(false);
  const [showRestoreEmployeeModal, setShowRestoreEmployeeModal] = useState(false);
  const [employeeToRestore, setEmployeeToRestore] = useState(null);
  const [restoreEmployeeSaving, setRestoreEmployeeSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [allEmployeesSelected, setAllEmployeesSelected] = useState(false);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [creatingReport, setCreatingReport] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [licenseInformation, setLicenseInformation] = useState(null);
  const [employeeProfiles, setEmployeeProfiles] = useState(null);
  const [usesSSO, setUsesSSO] = useState(false);
  const [searchParams, setSearchParams] = useState(initSearchParams);
  const [columnFilters, setColumnFilters] = useState({});
  const [showUpdateInternalPriceModal, setShowUpdateInternalPriceModal] = useState(false);
  const [showUpdateIntraCompanyPriceModal, setShowUpdateIntraCompanyPriceModal] = useState(false);
  const [showUpdateProfileModal, setShowUpdateProfileModal] = useState(false);
  const [showUpdateTagsModal, setShowUpdateTagsModal] = useState(false);

  useEffect(() => {
    const dataCache = DataCacheService.getDataCache();
    if (dataCache && dataCache.employee) {
      setCurrentUser(dataCache.employee);
    }
    const ssoProvider = dataCache?.ssoProvider;
    setUsesSSO(ssoProvider && ssoProvider > 0);
    reloadLicenseInformation();
    loadEmployeeProfiles();
  }, []);

  const openColumnConfigDialog = () => setShowColumnModal(true);
  const closeColumnConfigDialog = () => setShowColumnModal(false);
  const handleColumnSave = items => {
    const result = handleHiddenColumnsSelection(items, hideableColumns, columnSelectionStorageKey);
    setHideableColumns(result.hideableColumns);
    setColumnStatus(result.columnStatus);
    closeColumnConfigDialog();
  };

  const handleFilterChange = newFilters => {
    const resetSearchParams = { ...searchParams, page: 0, employeeFilters: newFilters };
    setSearchParams(resetSearchParams);
  };

  const handleResetFilters = () => {
    setSearchParams(prev => ({
      ...prev,
      page: 0,
      filter: {},
      search: {},
      select: {},
      employeeFilters: { ...defaultFilters },
      // forces refresh
      random: new Date().getTime(),
    }));
  };

  const reloadLicenseInformation = async () => {
    try {
      const { data } = await api.base.get(`/api/license/Info`);
      setLicenseInformation(data);
    } catch (error) {
      console.error('Error loading license information:', error);
      handleHttpError(intl)(error);
    }
  };
  const loadEmployeeProfiles = async () => {
    try {
      const { data } = await api.base.get('/api/employeeProfile/Get');
      const profiles = (data || [])
        .map(x => ({
          ...x,
          label: x.isDefault ? `${x.name} 🟊` : x.name,
        }))
        .sort(dynamicSort('name'));

      setEmployeeProfiles(profiles);
    } catch (error) {
      console.error('Error loading employee profiles:', error);
    }
  };

  const launchReport = async (path, parameters) => {
    setCreatingReport(true);
    try {
      const params = {
        ...parameters,
        culture: intl.locale,
        connectionId: SignalRService.getConnectionId ? SignalRService.getConnectionId() : null,
      };

      await api.base.get(`/api/${path}`, { params });

      ToastrService.success(intl.formatMessage(messages.ExportToExcelStarted));
    } catch (error) {
      handleHttpError(intl)(error);
    } finally {
      setCreatingReport(false);
    }
  };

  const handleExport = () =>
    launchReport('Employee/Report', {
      ...searchParams.employeeFilters,
      filter: JSON.stringify(columnFilters || {}),
    });

  const handleOvertimeExport = () => {
    setShowSelectPeriodModal(true);
  };

  const handleSelectPeriodSave = async periodData => {
    setSelectPeriodSaving(true);

    try {
      const reportFilters = {
        ...searchParams.employeeFilters,
        dateFrom: periodData.dateFrom,
        dateUntil: periodData.dateUntil,
        filter: JSON.stringify(columnFilters || {}),
      };

      await launchReport('Employee/OvertimeCorrection/Report', reportFilters);

      setShowSelectPeriodModal(false);
    } finally {
      setSelectPeriodSaving(false);
    }
  };

  const handleSelectPeriodClose = () => {
    setShowSelectPeriodModal(false);
    setSelectPeriodSaving(false);
  };

  const handleDeleteEmployee = employee => {
    setEmployeeToDelete(employee);
    setShowDeleteEmployeeModal(true);
  };

  const handleDeleteEmployeeConfirm = async ({ hidden }) => {
    if (!employeeToDelete) return;

    setDeleteEmployeeSaving(true);

    try {
      await api.base.delete(`/api/employee/delete`, {
        params: { hidden, id: employeeToDelete.employeeID },
      });

      setEmployees(prev =>
        prev.map(emp =>
          emp.employeeID === employeeToDelete.employeeID
            ? { ...emp, isActive: false, hidden }
            : emp,
        ),
      );

      setShowDeleteEmployeeModal(false);
      setEmployeeToDelete(null);
      reloadLicenseInformation();
      ToastrService.success(intl.formatMessage(messages.msgEmpRemoved));
    } catch (error) {
      handleHttpError(intl)(error);
    } finally {
      setDeleteEmployeeSaving(false);
    }
  };

  const handleDeleteEmployeeCancel = () => {
    setShowDeleteEmployeeModal(false);
    setEmployeeToDelete(null);
    setDeleteEmployeeSaving(false);
  };

  const handleRestoreEmployee = employee => {
    setEmployeeToRestore(employee);
    setShowRestoreEmployeeModal(true);
  };

  const handleRestoreEmployeeConfirm = async model => {
    if (!employeeToRestore) return;

    setRestoreEmployeeSaving(true);

    try {
      const { data } = await api.base.post('/api/Employee/Undelete', {
        employeeId: employeeToRestore.employeeID,
        ...model,
      });

      setEmployees(prev =>
        prev.map(emp =>
          emp.employeeID === employeeToRestore.employeeID
            ? { ...emp, ...data, isActive: true, hidden: false }
            : emp,
        ),
      );

      if (employeeToRestore.userID) {
        DataCacheService.updateUser(employeeToRestore.userID, {
          hidden: false,
          isActive: true,
        });
      }

      setShowRestoreEmployeeModal(false);
      setEmployeeToRestore(null);

      await reloadLicenseInformation();

      ToastrService.success(intl.formatMessage(messages.msgEmployeeRestoreSuccesful));
    } catch (error) {
      handleHttpError(intl)(error);
    } finally {
      setRestoreEmployeeSaving(false);
    }
  };

  const handleRestoreEmployeeCancel = () => {
    setShowRestoreEmployeeModal(false);
    setEmployeeToRestore(null);
    setRestoreEmployeeSaving(false);
  };

  const handleEmployeeUpdate = (employeeId, updates) => {
    setEmployees(prev =>
      prev.map(emp => (emp.employeeID === employeeId ? { ...emp, ...updates } : emp)),
    );
  };

  const handleSelectEmployee = employee => {
    setEmployees(prev =>
      prev.map(emp =>
        emp.employeeID === employee.employeeID ? { ...emp, selected: !emp.selected } : emp,
      ),
    );
  };

  const handleSelectAllEmployees = () => {
    const newValue = !allEmployeesSelected;
    setAllEmployeesSelected(newValue);
    setEmployees(prev => prev.map(emp => ({ ...emp, selected: newValue })));
  };

  const isGlobalActionsDisabled = () => {
    if (!employees || !employees.length) return true;
    if (employees.some(x => x.selected) || allEmployeesSelected) return false;
    return true;
  };

  const loadItems = async (searchParams = {}) => {
    try {
      const { sortKey: sort, employeeFilters, filter, ...restSearchParams } = searchParams;

      const params = {
        ...restSearchParams,
        sort,
        ...employeeFilters,
        filter: JSON.stringify(filter || {}),
      };

      userStorage.setItem(overviewParamsStorageKey, {
        table: searchParams,
        filters: employeeFilters,
      });

      const { data } = await api.base.get('/api/employee/Get', { params });

      const newItems = data.data.map(e => ({ ...e, selected: !!allEmployeesSelected }));

      if (searchParams.page > 0) {
        setEmployees(prev => [...prev, ...newItems]);
      } else {
        setEmployees(newItems);
        setTotalEmployees(data.total);
      }
      setColumnFilters(searchParams.filter);

      return data.data;
    } catch (e) {
      console.error('Error loading employees:', e);
      return [];
    }
  };
  const handleBatchUpdateAmount = (amountUpdate, url, regUpdate = {}) => {
    const params = {
      ...searchParams.employeeFilters,
      filter: JSON.stringify(columnFilters || {}),
    };
    const model = {
      amountUpdate,
      ...regUpdate,
      ids: employees.filter(e => e.selected !== allEmployeesSelected).map(e => e.employeeID),
      selectAll: allEmployeesSelected,
    };

    return api.base
      .post(url, model, { params })
      .then(() => {
        setShowUpdateInternalPriceModal(false);
        setShowUpdateIntraCompanyPriceModal(false);
        ToastrService.success();
        setSearchParams(prev => ({ ...prev, page: 0, random: new Date().getTime() }));
      })
      .catch(error => {
        handleHttpError(intl)(error);
        return '';
      });
  };

  const handleBatchUpdateProfile = profileId => {
    const params = {
      ...searchParams.employeeFilters,
      filter: JSON.stringify(columnFilters || {}),
    };
    const model = {
      profileId,
      ids: employees.filter(e => e.selected !== allEmployeesSelected).map(e => e.employeeID),
      selectAll: allEmployeesSelected,
    };

    return api.base
      .post('/api/Employee/BatchProfile', model, { params })
      .then(() => {
        setShowUpdateProfileModal(false);
        ToastrService.success();
        setSearchParams(prev => ({ ...prev, page: 0, random: new Date().getTime() }));
      })
      .catch(error => {
        handleHttpError(intl)(error);
        return '';
      });
  };

  const handleBatchUpdateTags = ({ action, tags }) => {
    const params = {
      ...searchParams.employeeFilters,
      filter: JSON.stringify(columnFilters || {}),
    };
    const model = {
      tags,
      ids: employees.filter(e => e.selected !== allEmployeesSelected).map(e => e.employeeID),
      selectAll: allEmployeesSelected,
    };
    const url = action === 'add' ? '/api/Employee/BatchAddTags' : '/api/Employee/BatchDeleteTags';

    return api.base
      .post(url, model, { params })
      .then(() => {
        setShowUpdateTagsModal(false);
        ToastrService.success();
        setSearchParams(prev => ({ ...prev, page: 0, random: new Date().getTime() }));
      })
      .catch(error => {
        handleHttpError(intl)(error);
        return '';
      });
  };

  const hasColumnFilters = Object.keys(columnFilters || {}).length > 0;

  return (
    <div id="EmployeeOverviewPage">
      <SimplePanel
        title={
          <span>
            <FormattedMessage {...messages.Employees} /> <ZendeskHelp article="5434820412434" />
          </span>
        }
        buttons={
          <div className="panel-buttons">
            {AuthService.hasPermission({ functionality: 'Users', privilege: 'Create' }) &&
              licenseInformation &&
              !licenseInformation.isTrial &&
              licenseInformation.billPerLicense &&
              !licenseInformation.disableUserCreation && (
                <Button
                  btnClassName="btn btn-default btn-sticky btn-sticky-left"
                  onClick={() => stateService.gotoState('employees.tmp')}
                  title={intl.formatMessage(messages.AddTemporaryEmployee)}
                >
                  <i className="fa fa-plus"></i>
                  <span>
                    <FormattedMessage {...messages.AddTemporaryEmployee} />
                  </span>
                </Button>
              )}
            {AuthService.hasPermission({ functionality: 'Users', privilege: 'Create' }) &&
              licenseInformation &&
              !licenseInformation.disableUserCreation && (
                <Button
                  btnClassName="btn btn-inverse btn-sticky btn-sticky-left"
                  onClick={() => stateService.gotoState('employees.new')}
                  title={intl.formatMessage(messages.AddEmployee)}
                >
                  <i className="fa fa-plus"></i>
                  <span>
                    <FormattedMessage {...messages.AddEmployee} />
                  </span>
                </Button>
              )}
          </div>
        }
      >
        {licenseInformation && licenseInformation.billPerLicense && (
          <div className="info-block">
            <div className="flex-container">
              <div className="flex-child-block">
                <span className="center">
                  <h2>
                    <FormattedMessage {...messages.Employees} />
                  </h2>
                  <h1 className="bold">{totalEmployees}</h1>
                </span>
              </div>
              <div className="flex-child-block separator">
                <span className="center">
                  <h2>
                    <FormattedMessage {...messages.TotalLicenses} />
                  </h2>
                  <h1 className="bold">{licenseInformation.totalLicenses || 0}</h1>
                </span>
              </div>
              <div className="flex-child-block separator">
                <span className="center">
                  <h2>
                    <FormattedMessage {...messages.Assigned} />
                  </h2>
                  <h1 className="bold">{licenseInformation.assignedLicenses || 0}</h1>
                </span>
              </div>
              <div className="flex-child-block separator">
                <span className="center">
                  <h2>
                    <FormattedMessage {...messages.Available} />
                  </h2>
                  <h1 className="bold">{licenseInformation.availableLicenses || 0}</h1>
                </span>
              </div>
              {!!licenseInformation.totalTemporaryLicenses && (
                <>
                  <div className="flex-child-block separator">
                    <span className="center">
                      <h2>
                        <FormattedMessage {...messages.TotalTemporary} />
                      </h2>
                      <h1 className="bold">{licenseInformation.totalTemporaryLicenses}</h1>
                    </span>
                  </div>
                  <div className="flex-child-block separator">
                    <span className="center">
                      <h2>
                        <FormattedMessage {...messages.AssignedTemporary} />
                      </h2>
                      <h1 className="bold">{licenseInformation.assignedTemporaryLicenses || 0}</h1>
                    </span>
                  </div>
                  <div className="flex-child-block separator">
                    <span className="center">
                      <h2>
                        <FormattedMessage {...messages.AvailableTemporary} />
                      </h2>
                      <h1 className="bold">{licenseInformation.availableTemporaryLicenses || 0}</h1>
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <EmployeeOverviewFilters
          filters={searchParams.employeeFilters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          hasColumnFilters={hasColumnFilters}
          intl={intl}
          employeeProfiles={employeeProfiles}
        />
      </SimplePanel>

      <div className="row btn-row hidden-xs">
        <div className="col-md-12 content-right margin10R">
          <Button btnClassName="btn btn-xs btn-default margin10R" onClick={openColumnConfigDialog}>
            <i className="fa fa-cog"></i>
            <span className="margin5L">
              <FormattedMessage {...messages.ColumnConfig} />
            </span>
          </Button>

          <div className="btn-group">
            <Button
              btnClassName="btn btn-xs btn-default dropdown-toggle"
              onClick={() => {}}
              disabled={creatingReport}
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <span className="margin5R">
                {creatingReport ? (
                  <i className="fa fa-spinner fa-spin"></i>
                ) : (
                  <i className="fa fa-file-excel-o"></i>
                )}
                <span>
                  <FormattedMessage {...messages.Export} />
                </span>
              </span>
              <span className="caret"></span>
            </Button>
            <ul className="dropdown-menu" style={{ left: 'auto', right: 0 }}>
              <li>
                <a
                  onClick={() => !creatingReport && totalEmployees > 0 && handleExport()}
                  style={{ cursor: 'pointer' }}
                  title={intl.formatMessage({
                    id: 'Export employees to Excel',
                    defaultMessage: 'Export employees to Excel',
                  })}
                >
                  <span>
                    <FormattedMessage {...messages.ExportEmployeesToExcel} />
                  </span>
                </a>
              </li>
              <li>
                <a
                  onClick={() => !creatingReport && totalEmployees > 0 && handleOvertimeExport()}
                  style={{ cursor: 'pointer' }}
                  title={intl.formatMessage({
                    id: 'Export overtime corrections to Excel',
                    defaultMessage: 'Export overtime corrections to Excel',
                  })}
                >
                  <span>
                    <FormattedMessage {...messages.ExportOvertimeCorrectionsToExcel} />
                  </span>
                </a>
              </li>
            </ul>
          </div>

          <EmployeeGlobalActionsButton
            disabled={isGlobalActionsDisabled()}
            onUpdateInternalPrice={() => setShowUpdateInternalPriceModal(true)}
            onUpdateIntraCompanyPrice={() => setShowUpdateIntraCompanyPriceModal(true)}
            onUpdateProfile={() => setShowUpdateProfileModal(true)}
            onUpdateTags={() => setShowUpdateTagsModal(true)}
          />
        </div>
      </div>
      {!!licenseInformation && !!employeeProfiles && (
        <div className="margin10T">
          <ScrollableTable
            tableClassName="table table-grid table-hover margin50B"
            items={employees}
            loadItems={searchParams => loadItems(searchParams)}
            initialSearchParams={searchParams}
            renderRow={employee => (
              <EmployeeOverviewRow
                key={employee.employeeID}
                item={employee}
                columnStatus={columnStatus}
                currentUser={currentUser}
                stateService={stateService}
                employeeProfiles={employeeProfiles}
                onDeleteEmployee={handleDeleteEmployee}
                onRestoreEmployee={handleRestoreEmployee}
                onEmployeeUpdate={handleEmployeeUpdate}
                onSelectEmployee={handleSelectEmployee}
                usesSSO={usesSSO}
                filters={searchParams.employeeFilters}
                licenseInformation={licenseInformation}
              />
            )}
          >
            <ColumnHeader
              className="checkbox-column sticky-header"
              title={
                <Checkbox
                  extraClass="margin2L"
                  onClick={handleSelectAllEmployees}
                  value={allEmployeesSelected}
                />
              }
            />
            <ColumnHeader
              title={intl.formatMessage(messages.Name)}
              sortKey="lastName"
              className="sticky-header"
              searchParams={searchParams}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.Phone)}
              sortKey="employeePhone"
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.phone}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.Email)}
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.email}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.DateOfBirth)}
              sortKey="employeeDateOfBirth"
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.dateOfBirth}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.PriceProfile)}
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.profile}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.OwnCompany)}
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.company}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.Teams)}
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.teams}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.Role)}
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.role}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.EmployeeType)}
              sortKey="employeeType"
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.employeeType}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.EmployeeNumber)}
              sortKey="employeeNumber"
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.employeeNumber}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.InternalPrice)}
              sortKey="internalPrice"
              className="sticky-header text-right"
              searchParams={searchParams}
              filter={{ type: 'number', key: 'internalPrice' }}
              visible={columnStatus.internalPrice}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.IntraCompanyPrice)}
              sortKey="intraCompanyPrice"
              className="sticky-header text-right"
              searchParams={searchParams}
              filter={{ type: 'number', key: 'intraCompanyPrice' }}
              visible={columnStatus.intraCompanyPrice}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.EmployedSince)}
              sortKey="employeeStartDate"
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.startDate}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.EndDate)}
              sortKey="endDate"
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.endDate}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.EmploymentStartDate)}
              sortKey="employmentStartDate"
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.employmentStartDate}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.EmploymentEndDate)}
              sortKey="employmentEndDate"
              className="sticky-header"
              searchParams={searchParams}
              visible={columnStatus.employmentEndDate}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.License)}
              className="sticky-header text-center"
              searchParams={searchParams}
              visible={columnStatus.license}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.Confirmed)}
              className="sticky-header text-center"
              searchParams={searchParams}
              visible={columnStatus.confirmed}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.TwoFaEnabled)}
              className="sticky-header text-center"
              searchParams={searchParams}
              visible={columnStatus.twoFaEnabled}
            />
            {AuthService.hasPermission({ functionality: 'Users', privilege: 'Update' }) &&
              !usesSSO && (
                <ColumnHeader
                  title={intl.formatMessage(messages.Reset)}
                  className="sticky-header text-center"
                  searchParams={searchParams}
                />
              )}
            {AuthService.hasPermission({ functionality: 'Users', privilege: 'Update' }) &&
              searchParams.employeeFilters.activeEmployeeFilter !== 1 && (
                <ColumnHeader
                  title={intl.formatMessage(messages.Visible)}
                  className="sticky-header text-center"
                  searchParams={searchParams}
                />
              )}
            {AuthService.hasPermission({ functionality: 'Users', privilege: 'Delete' }) && (
              <ColumnHeader
                title={intl.formatMessage(messages.Delete)}
                className="sticky-header text-center"
                searchParams={searchParams}
              />
            )}
          </ScrollableTable>
        </div>
      )}
      {showColumnModal ? (
        <CheckboxListModal
          show
          onClose={closeColumnConfigDialog}
          onSave={handleColumnSave}
          items={hideableColumns.map(col => ({
            id: col.id,
            label: col.label,
            selected: !!col.selected,
          }))}
          title={intl.formatMessage(messages.SelectColumns)}
        />
      ) : null}

      {showSelectPeriodModal ? (
        <SelectPeriodModal
          show
          onClose={handleSelectPeriodClose}
          onSave={handleSelectPeriodSave}
          saving={selectPeriodSaving}
        />
      ) : null}

      {showDeleteEmployeeModal ? (
        <DeleteEmployeeModal
          show
          employee={employeeToDelete}
          onClose={handleDeleteEmployeeCancel}
          onSave={handleDeleteEmployeeConfirm}
          saving={deleteEmployeeSaving}
        />
      ) : null}

      {showRestoreEmployeeModal ? (
        <RestoreEmployeeModal
          show
          employee={employeeToRestore}
          onClose={handleRestoreEmployeeCancel}
          onSave={handleRestoreEmployeeConfirm}
          saving={restoreEmployeeSaving}
        />
      ) : null}

      {showUpdateInternalPriceModal ? (
        <UpdateEmployeeAmountModal
          show
          intl={intl}
          title={intl.formatMessage(messages.UpdateInternalPrice)}
          onClose={() => setShowUpdateInternalPriceModal(false)}
          onSave={(amountUpdate, regUpdate) =>
            handleBatchUpdateAmount(amountUpdate, '/api/Employee/BatchInternalPrice', regUpdate)
          }
          exampleAmount={employees.find(e => e.selected)?.internalPrice}
          showRecalcRegistrations
        />
      ) : null}

      {showUpdateIntraCompanyPriceModal ? (
        <UpdateEmployeeAmountModal
          show
          intl={intl}
          title={intl.formatMessage(messages.UpdateIntraCompanyPrice)}
          onClose={() => setShowUpdateIntraCompanyPriceModal(false)}
          onSave={amountUpdate =>
            handleBatchUpdateAmount(amountUpdate, '/api/Employee/BatchIntraCompanyPrice')
          }
          exampleAmount={employees.find(e => e.selected)?.intraCompanyPrice}
        />
      ) : null}

      {showUpdateProfileModal ? (
        <UpdateEmployeeProfileModal
          show
          intl={intl}
          onClose={() => setShowUpdateProfileModal(false)}
          onSave={handleBatchUpdateProfile}
          profiles={employeeProfiles}
        />
      ) : null}

      {showUpdateTagsModal && (
        <UpdateEmployeeTagsModal
          intl={intl}
          show={showUpdateTagsModal}
          onClose={() => setShowUpdateTagsModal(false)}
          onSave={handleBatchUpdateTags}
        />
      )}
    </div>
  );
};

EmployeeOverviewContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(EmployeeOverviewContainer));
